import { Fragment, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../../redux/store"
import { selectCurrentCustomerNumber } from "../../../../redux/customer"
import { Box, Card, useTheme, Link, ClickAwayListener } from "@mui/material"
import LoopIcon from "@mui/icons-material/Loop"
import NotInterestedIcon from '@mui/icons-material/NotInterested'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import CompliancePlipLight from "../../../../components/CompliancePlipLight"
import ExpirationDate from "./ExpirationDate"
import DiscontinuedExpirationDate from "./DiscontinuedExpirationDate"
import BackOrderedItemMessage from "./BackOrderedItemMessage"
import { LightTooltip, HtmlTooltip } from "../../dentalenhancements/components/ToolTip"
import { getItemImage, getRefillStatus, getTooltipTitle } from "./utils/helpers"
import { getSdsLink, isOrderProcessing } from "./utils/helpers"
import { MedComplianceCode } from "../medications/enums"
import { KitItem } from './types'
import { addError, addWarning } from "../../../../redux/alerts"
import { useNavigate } from "react-router-dom"
import useIsCorpView from "../../../../hooks/useIsCorpView"
import { ItemType, KitRefillStatus } from "./enums"

type KitItemContainer = {
  kitItem: KitItem,
  defaultIcon: string,
}

const KitItemContainer = (props: KitItemContainer) => {
  const {
    kitItem,
    defaultIcon,
  } = props

  const theme = useTheme()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const isCorpView = useIsCorpView()
  const customerNumber = useAppSelector(selectCurrentCustomerNumber)

  const [openTooltip, setOpenTooltip] = useState(false)

  const handleTooltipClose = () => {
    setOpenTooltip(false)
  }

  const handleTooltipOpen = () => {
    setOpenTooltip(!openTooltip)
  }

  const handleSDS = async () => {
    try {
      if (kitItem.type === ItemType.Medication) {
        const link = await getSdsLink(customerNumber, kitItem.itemNumber)

        if (link) window.open(link, '_blank')
        else {
          dispatch(addWarning('We are sorry, the SDS you have requested is currently unavailable. Please check back in the next week. If you have questions, please email us at ontraq@healthfirst.com'))
        }
      }
    } catch (error: any) {
      console.error(error)
      if (error?.response?.status === 404) {
        dispatch(addWarning('We are sorry, the SDS you have requested is currently unavailable. Please check back in the next week. If you have questions, please email us at ontraq@healthfirst.com'))
      } else {
        dispatch(addError(`There was an issue with your request, please try again later.`));
      }    }
  }

  const refillStatus = getRefillStatus(kitItem)

  const refillStatusIcons = new Map([
    [KitRefillStatus.refillOn, <LoopIcon sx={{ color: theme.palette.success.main }} fontSize="small" />],
    [KitRefillStatus.refillOff, <LoopIcon sx={{ color: theme.palette.inactive.main }} fontSize="small" />],
    [KitRefillStatus.discontinued, <NotInterestedIcon sx={{ color: theme.palette.inactive.main }} fontSize="small" />],
  ])

  return (
    <>
      <Card className="flex flex-space-between flex-align-center" sx={{ p: 2, color: theme.palette.color50555b.main }} variant="outlined">
        <Box className="flex flex-align-center">
          <img
            alt="kit item"
            className="m-l-16 m-r-32"
            src={getItemImage(kitItem.image) ?? defaultIcon}
            style={{ width: '64px', maxHeight: '64px', objectFit: 'contain' }}
          />
          <Box>
            <Box className="flex flex-align-center c-g-8">
              <CompliancePlipLight medium={true} status={kitItem.status} message={kitItem.messageShort} />
              <span className="f-s-17">
                { kitItem.quantity } { kitItem.name }
              </span>
            </Box>
            <Box className="f-s-13">
              {
                kitItem.lotNumber
                  && <Box>Lot #: { kitItem.lotNumber ?? '' }</Box>
              }
              <Box>Item #: { kitItem.itemNumber ?? '' }</Box>
              {
                kitItem.discontinued && kitItem.refillStatus !== 0
                  && (
                      <DiscontinuedExpirationDate date={kitItem.expirationDate} status={kitItem.complianceStatus}>
                        {
                          kitItem.discontinued && kitItem.isExpired
                            ? (
                              <LightTooltip
                                arrow={true}
                                placement="top"
                                title="This kit will be removed from OnTraq after it hits it's 3 days expiration period. Therefore, please remove/discard this expired item from your kit physically if you haven't already."
                              >
                                <InfoOutlinedIcon sx={{ color: theme.palette.common.black, fontSize: '1rem' }} fontSize="small" />
                              </LightTooltip>
                            ) : null
                        }
                      </DiscontinuedExpirationDate>
                    )
              }
              {
                !kitItem.discontinued
                  && <ExpirationDate date={kitItem.expirationDate} status={kitItem.complianceStatus} refill={kitItem.refillStatus} />
              }
              {
                kitItem.complianceCode === MedComplianceCode.Unshipped
                  && <span>Will ship when available</span>
              }
              {
                isOrderProcessing(kitItem)
                  &&  (
                        <>
                          {" - "}
                          <span className="inline-flex flex-align-center c-g-8">
                            <strong>Order processing</strong>
                            <LightTooltip
                              arrow={true}
                              placement="top"
                              title="A refill for this item was approved and is shipping soon."
                            >
                              <HelpOutlineIcon sx={{ color: theme.palette.common.black, fontSize: '1rem', marginRight: '4px' }} fontSize="small" />
                            </LightTooltip>  
                          </span>                      
                        </>
                      )
              }
              {
                kitItem.type === ItemType.Medication && kitItem.isBackordered && kitItem.refillStatus !== 0
                  &&  (
                      <ClickAwayListener onClickAway={handleTooltipClose}>
                        <span>
                          <HtmlTooltip
                            arrow={true}
                            placement="top"
                            title={
                              <Fragment>
                                <BackOrderedItemMessage />
                              </Fragment>
                            }
                            PopperProps={{
                              disablePortal: true,
                            }}
                            onClose={handleTooltipClose}
                            open={openTooltip}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                          >
                            <span className="text-info pointer" onClick={handleTooltipOpen}>{" This item is currently on backorder"}</span>
                          </HtmlTooltip>
                        </span>
                      </ClickAwayListener>
                    )
              }
              {
                kitItem.type === ItemType.Device && kitItem.isBackordered && (
                  <Box sx={{color: theme.palette.inactive.main}}>This item is currently on backorder</Box>
                )
              }
              <Box>
                {
                  kitItem.nextRefillDate && kitItem.complianceCode !== MedComplianceCode.Unshipped
                    &&  (
                        <span>
                          {
                            isCorpView
                              ? <span>Refill in { kitItem.nextRefillDate }</span>
                              : <Link sx={{ color: theme.palette.linkColor.main }} component="button" onClick={() => navigate('/practice/shipments/upcoming')}>
                                  Refill in { kitItem.nextRefillDate }
                                </Link>
                          }
                        </span>
                      )
                }
                {
                  kitItem.type === ItemType.Medication && kitItem.sds && (
                    <span className="print-hide">
                      {
                        Boolean(kitItem.nextRefillDate) && <span> | </span>
                      }
                      <Link className="pointer" sx={{ color: theme.palette.linkColor.main }} onClick={handleSDS}>
                        SDS
                      </Link>
                    </span>
                  )
                }
              </Box>
            </Box>
          </Box>
        </Box>
        {
          refillStatus && refillStatusIcons.has(refillStatus)
            && (
                <Box>
                  <LightTooltip placement="left" arrow={true} title={getTooltipTitle(refillStatus)}>
                    { refillStatusIcons.get(refillStatus)! }
                  </LightTooltip>
                </Box>
            )
        }
      </Card>
    </>
  )
}

export default KitItemContainer
